<script setup lang="ts">

import { ref } from 'vue';
import { isNullOrUndefined } from 'is-what';
import { Field } from "@library/Components/Forms/Form";
import { makeForm } from '@library/Components/Forms/Form';
import { useUserSettings } from '@services/UserSettings';
import { VehicleHeaderRow } from "../../Components/Utilities";

type FormData = {
    latitude: number | undefined,
    longitude: number | undefined
}

/**
 * Validates the input only contains integer or decimal numbers.
 * TODO: When time allows for end-to-end testing, we need to update the version of the CV Library and replace this with the validator in the latest CV Library version (1.116.0)
 */
 function decimalNumbersOnly(): ( value: any ) => true | string
{
    return ( value: any ) =>
    {
        // we dont want to validate empty input as it may not be a mandatory field
        if( value == null || value == '' ) return true;

        return /^-?\d*(\.\d*)?$/.test( value ) || "Must be a number";
    };
}

const userSettings = useUserSettings();
const hasCustomGps = ref( userSettings.enableCustomGpsCoordinates ); // Maintain independently as we need to ensure that we have long/lat before saving details.
const saved = ref( false );
const formData: FormData = {
    latitude: userSettings.customGpsCoordinates?.latitude,
    longitude: userSettings.customGpsCoordinates?.longitude
};

const formModel = makeForm( formData );

const validation = {
    longitude: [ Field.required( "Enter a longitude" ), decimalNumbersOnly() ],
    latitude: [ Field.required( "Enter a latitude" ), decimalNumbersOnly() ]
};

const applyCoordinates = ( data: FormData ) =>
{
    if ( !isNullOrUndefined( data.longitude ) && !isNullOrUndefined( data.latitude ) )
    {
        userSettings.enableCustomGpsCoordinates = true;
        userSettings.customGpsCoordinates = { 
            longitude: Number( data.longitude ),
            latitude: Number( data.latitude )
        };

        saved.value = true;
    }
};

const handleSwitchToggle = ( event: { target: HTMLInputElement } ) =>
{
    if ( event.target.checked === false )
    {
        userSettings.enableCustomGpsCoordinates = false;
    }
    else if ( userSettings.customGpsCoordinates )
    {
        // Only set to true if we also have GPS coordinates.
        userSettings.enableCustomGpsCoordinates = true;
    }
};

</script>

<template>

    <div class="border-t border-gray-900/50">

        <VehicleHeaderRow class="px-4 lg:px-6 gap-x-4 3xl:gap-x-8 gap-y-3 3xl:gap-y-4">

            <div class="flex-grow">
                <h1 class="tracking-wider">
                    Has Custom Location
                </h1>
                <div class="text-sm 3xl:text-base text-gray-500 -mt-0.5 uppercase">
                    Use custom GPS co-ordinates
                </div>
            </div>


            <Switch v-model="hasCustomGps"
                    color="secondary"
                    density="compact"
                    class="mr-3"
                    @change="handleSwitchToggle"
                    hide-details />
        </VehicleHeaderRow>

        <div class="w-full px-4 lg:px-6 pb-4" 
             :class="{ 'hidden': !hasCustomGps }">

            <Form :form="formModel"
                v-slot="{ form, model }"
                @ok="applyCoordinates">

                <div class="grid gap-4 grid-cols-2">
                    <TextField label="Latitude"
                               v-model="model.latitude"
                               :rules="validation.latitude"
                               :required="true"
                               @update:modelValue="() => saved = false">
                    </TextField>
                    <TextField label="Longitude"
                               v-model="model.longitude"
                               :rules="validation.longitude"
                               :required="true"
                               @update:modelValue="() => saved = false">
                    </TextField>
                </div>

                <p class="text-center text-xs 3xl:text-sm opacity-60 pb-3 -mt-2">*Use negative Latitude for southern hemisphere</p>

                <div class="text-right">
                    <Icon v-if="saved" name="check" class="ml-2 text-2xl animate-ping animate-once-only" />
                    <Button variant="flat"
                            color="secondary"
                            class="ml-4"
                            :disabled="form.isInvalid || form.isClean || !hasCustomGps || saved"
                            type="submit">
                        Apply
                    </Button>
                </div>

            </Form>
        </div>
    </div>

</template>